import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, Youtube, Mail } from "lucide-react";
import { WafaLogo, AltalibLogo } from "@/components/WafaLogo";
import { MeyarLogo } from "@/components/MeyarLogo";
import { WhatsAppButton, WhatsAppIcon } from "@/components/WhatsAppButton";
import { useWhatsAppLink, WA_LABEL } from "@/lib/whatsapp";
import { useI18n } from "@/lib/i18n";

const COLUMNS = [
  {
    title: "Services",
    links: [
      { label: "Custom software", to: "/services/custom-software-development-company-in-oman" },
      { label: "SaaS products", to: "/services/saas-development-company-in-oman" },
      { label: "Mobile apps", to: "/services/mobile-app-development-company-in-oman" },
      { label: "AI & automation", to: "/ai-lab" },
      { label: "Data & analytics", to: "/ai-lab" },
      { label: "Managed run & support", to: "/services/application-support-maintenance-oman" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Delivery & logistics", to: "/solutions/on-demand-delivery-platform" },
      { label: "ERP", to: "/services/erp-software-development-company-in-oman" },
      { label: "Warehouse management", to: "/solutions/warehouse-management" },
      { label: "Showroom management", to: "/solutions/showroom-management" },
      { label: "Marketplace commerce", to: "/solutions/e-commerce-marketplace" },
      { label: "PDPL & compliance", to: "/company/pdpl-compliance-oman" },
    ],
  },
  {
    title: "Meyar",
    links: [
      { label: "Meyar overview", to: "/meyar" },
      { label: "Enterprise GRC", to: "/meyar/enterprise-grc" },
      { label: "Cyber Security GRC", to: "/meyar/cyber-security-grc" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/company/about-us" },
      { label: "Work", to: "/work" },
      { label: "Insights", to: "/resources/blog" },
      { label: "Podcast", to: "/resources/podcast" },
      { label: "Careers", to: "/company/careers" },
      { label: "How we work", to: "/company/how-we-work" },
    ],
  },
];

export function Footer() {
  const { t, lang } = useI18n();
  const wa = useWhatsAppLink("footer_social");

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-wafa-deep text-white">
      <div className="mx-auto max-w-7xl px-4 py-16 lg:px-6 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <WafaLogo variant="knockout" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">
              An Oman-based technology company. We design, build and secure custom platforms, SaaS
              products and mobile apps — then we stay and run them.
            </p>

            <div className="mt-8">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-white/55">
                Technology partner to
              </div>
              <div className="mt-2">
                <AltalibLogo />
              </div>
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              {col.title === "Meyar" ? (
                <div className="mb-4"><MeyarLogo variant="knockout" className="h-5" /></div>
              ) : (
                <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
                  {col.title}
                </h4>
              )}
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={`${col.title}-${l.label}`}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.to}`}
                      className="text-sm text-white/70 transition-colors hover:text-wafa-sunshine"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">Get in touch</h4>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-wafa-primary" />
                <a href={`mailto:${t.contact.email}`} data-testid="footer-email" className="hover:text-wafa-sunshine">
                  {t.contact.email}
                </a>
              </li>
              <li>
                <WhatsAppButton variant="knockout" className="mt-1 w-full justify-center" label={WA_LABEL[lang]} source="footer" />
              </li>
            </ul>
          </div>
        </div>

        {/* Trademark / integrations legal footnote — used across the site. */}
        <p className="mt-14 max-w-4xl text-[11px] leading-relaxed text-white/45" data-testid="footer-trademark-notice">
          All product names, logos and brands referenced on this site are the property of their
          respective owners. Their use indicates compatibility only and does not imply endorsement
          or affiliation.
        </p>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 pb-36 pt-6 text-xs text-white/60 sm:flex-row sm:items-center md:pb-24 lg:px-6">
          <div>© 2026 Wafa Solutions LLC. Registered in the Sultanate of Oman.</div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white" data-testid="footer-terms">Terms of Service</Link>
            <Link to="/cookie-policy" className="hover:text-white" data-testid="footer-cookies">Cookie Policy</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("wafa:open-cookie-settings"))}
              className="hover:text-white"
              data-testid="footer-cookie-settings"
            >
              Cookie Settings
            </button>
            <Link to="/sitemap" className="hover:text-white" data-testid="footer-sitemap">Sitemap</Link>
            <span className="ms-2 flex items-center gap-3">
              <a href="#" aria-label="LinkedIn" data-testid="social-linkedin" className="hover:text-white"><Linkedin className="h-4 w-4" /></a>
              <a href="#" aria-label="Facebook" data-testid="social-facebook" className="hover:text-white"><Facebook className="h-4 w-4" /></a>
              <a href="#" aria-label="Instagram" data-testid="social-instagram" className="hover:text-white"><Instagram className="h-4 w-4" /></a>
              <a href="#" aria-label="YouTube" data-testid="social-youtube" className="hover:text-white"><Youtube className="h-4 w-4" /></a>
              {wa ? (
                <a href={wa.href} onClick={wa.onClick} target="_blank" rel="noopener noreferrer" aria-label={WA_LABEL[lang]} data-testid="social-whatsapp" className="hover:text-white">
                  <WhatsAppIcon size={16} />
                </a>
              ) : null}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
